import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Paper,
  Divider,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { usePlacesFilters } from '../hooks/usePlacesFilters';
import { useUiStore } from '../stores/uiStore';

const TAGS = [
  "calisthenics",
  "weightlifting",
  "aerobics",
  "yoga",
  "tennis",
  "football",
  "volleyball",
  "table tennis",
  "chess",
  "running and jogging",
  "skating and blading",
  "surfing",
  "basketball",
  "general mobility",
  "hiking and trailing",
  "racing and carting",
  "kid playground",
];

const AGE_CATEGORIES = ["baby", "child", "teen", "adult", "elderly"];

/**
 * Filter content component (reusable for both panel and drawer).
 */
const FilterContent: React.FC = () => {
  const { filters, updateFilter, clearFilters } = usePlacesFilters();

  const handleTypeChange = (value: string) => {
    updateFilter('type', value === 'All' ? null : value);
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];
    updateFilter('tags', newTags);
  };

  const handleAgeCategoryToggle = (category: string) => {
    const currentAgeCategory = filters.age_category || [];
    const newAgeCategory = currentAgeCategory.includes(category)
      ? currentAgeCategory.filter((c) => c !== category)
      : [...currentAgeCategory, category];
    updateFilter('age_category', newAgeCategory);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Filters
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Type Filter */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={filters.type || 'All'}
          onChange={(e) => handleTypeChange(e.target.value)}
          label="Type"
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Outdoor">Outdoor</MenuItem>
          <MenuItem value="Indoor">Indoor</MenuItem>
        </Select>
      </FormControl>

      {/* Tags Filter */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Tags
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {TAGS.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              clickable
              color={filters.tags?.includes(tag) ? 'primary' : 'default'}
              onClick={() => handleTagToggle(tag)}
              sx={{ minHeight: '44px', minWidth: '44px' }} // 44px minimum touch target
            />
          ))}
        </Box>
      </Box>

      {/* Age Category Filter */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Age Category
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {AGE_CATEGORIES.map((category) => (
            <Chip
              key={category}
              label={category}
              clickable
              color={filters.age_category?.includes(category) ? 'primary' : 'default'}
              onClick={() => handleAgeCategoryToggle(category)}
              sx={{ minHeight: '44px', minWidth: '44px' }} // 44px minimum touch target
            />
          ))}
        </Box>
      </Box>

      {/* Inclusivity Filters */}
      <FormGroup sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.kid_friendly === true}
              onChange={(e) => updateFilter('kid_friendly', e.target.checked ? true : null)}
            />
          }
          label="Kid Friendly"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.disability_friendly === true}
              onChange={(e) => updateFilter('disability_friendly', e.target.checked ? true : null)}
            />
          }
          label="Disability Friendly"
        />
      </FormGroup>

      {/* Clear Filters Button */}
      <Button 
        variant="outlined" 
        color="secondary" 
        onClick={clearFilters} 
        fullWidth
        sx={{ minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
      >
        Clear Filters
      </Button>
    </>
  );
};

/**
 * Filter panel component with filter inputs.
 * Mobile: SwipeableDrawer bottom sheet
 * Desktop: Standard Paper panel
 */
export const FilterPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <768px
  const isFilterModalOpen = useUiStore((state) => state.isFilterModalOpen);
  const openFilterModal = useUiStore((state) => state.openFilterModal);
  const closeFilterModal = useUiStore((state) => state.closeFilterModal);

  const filterContent = <FilterContent />;

  // Mobile: Bottom sheet with sticky Apply button
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={isFilterModalOpen}
        onClose={closeFilterModal}
        onOpen={openFilterModal}
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {filterContent}
        </Box>
        {/* Sticky Apply button */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={closeFilterModal}
            sx={{ minHeight: '48px', transition: 'all 0.2s ease-in-out' }} // 48px > 44px requirement
          >
            Apply Filters
          </Button>
        </Box>
      </SwipeableDrawer>
    );
  }

  // Desktop: Standard panel
  return (
    <Paper sx={{ p: 3, mb: 2, borderRadius: 1 }}>
      {filterContent}
    </Paper>
  );
};
